import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  Stack,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import { useOffline } from './OfflineProvider';
import { db, OpRow } from './db';
import { countCascadeChildren } from './opQueue';

const OP_LABEL: Record<string, string> = { create: 'Created', update: 'Edited', delete: 'Deleted' };

/** Vet-readable names for the raw sync entity types. */
const ENTITY_LABEL: Record<string, string> = {
  horse: 'Patient',
  client: 'Client',
  appointment: 'Appointment',
  soapNote: 'SOAP note',
  medicalRecord: 'Visit note / record',
  medication: 'Medication',
  vaccination: 'Vaccination',
  procedure: 'Procedure',
  dischargeSummary: 'Discharge summary',
  invoice: 'Invoice',
  payment: 'Payment',
  clientCommunication: 'Client message',
  clientNote: 'Client note',
  inventoryItem: 'Inventory item',
  labResult: 'Lab result',
  problemList: 'Problem',
  recallReminder: 'Reminder',
  timeBlock: 'Time block',
};

/** Internal/meaningless-to-a-vet payload keys hidden from the field list. */
const HIDDEN_FIELDS = new Set([
  'id',
  'clinicId',
  'horseId',
  'clientId',
  'invoiceId',
  'appointmentId',
  'providerId',
  'assignedVetId',
  'createdAt',
  'updatedAt',
  '_pendingSync',
]);

function fieldList(payload: any): string {
  if (!payload || typeof payload !== 'object') return '';
  return Object.entries(payload)
    .filter(([k, v]) => !HIDDEN_FIELDS.has(k) && v != null && typeof v !== 'object')
    .slice(0, 5)
    .map(([k, v]) => `${k}: ${String(v).slice(0, 60)}`)
    .join(' · ');
}

/**
 * One human line identifying WHAT record this op is about, resolved from the
 * payload + replica — without it, two failed cash payments are
 * indistinguishable in the list.
 */
async function summarizeOp(op: OpRow): Promise<string> {
  try {
    const p: any = op.payload ?? {};
    if (op.entityType === 'payment') {
      const inv = p.invoiceId ? await db.invoices.get(p.invoiceId) : undefined;
      const horse = inv?.horseId ? await db.horses.get(inv.horseId as string) : undefined;
      const amount = typeof p.amount === 'number' ? `$${p.amount.toFixed(2)}` : '';
      return [amount, p.paymentType, inv?.invoiceNumber ?? 'new invoice', horse?.name]
        .filter(Boolean)
        .join(' — ');
    }
    const horseId = p.horseId ?? (op.table === 'horses' ? op.entityId : undefined);
    if (horseId) {
      const horse = await db.horses.get(horseId);
      if (horse?.name) return `${horse.name}`;
    }
    if (p.name) return `${p.name}`;
    if (p.invoiceNumber) return `${p.invoiceNumber}`;
    return '';
  } catch {
    return '';
  }
}

/**
 * Lists parked offline writes (conflicts + errors) and lets the vet resolve each:
 * conflicts → Keep mine / Use server's; errors → Retry / Discard. Opened from the
 * OfflineIndicator. Whole-record resolution (field-level merge is a future refinement).
 */
export const ConflictReviewDialog: React.FC<{ open: boolean; onClose: () => void }> = ({
  open,
  onClose,
}) => {
  const { reviewOps, resolveReview } = useOffline();
  const [summaries, setSummaries] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const next: Record<string, string> = {};
      for (const op of reviewOps) next[op.id] = await summarizeOp(op);
      if (!cancelled) setSummaries(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, reviewOps]);

  const handleDiscard = async (op: OpRow) => {
    // Discarding a parent create cascades to its queued children (e.g. a failed
    // offline invoice takes its recorded cash payment with it) — disclose that
    // before destroying field work. No undo.
    const children = op.operationType === 'create' ? await countCascadeChildren(op.entityId) : 0;
    const label = ENTITY_LABEL[op.entityType] ?? op.entityType;
    const cascade =
      children > 0
        ? ` This will ALSO discard ${children} related queued change${children === 1 ? '' : 's'} (e.g. a payment recorded against it).`
        : '';
    if (
      window.confirm(
        `Permanently discard this unsynced ${label.toLowerCase()}? It was never saved to the server and cannot be recovered.${cascade}`,
      )
    ) {
      resolveReview(op.id, 'discard');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Review unsynced changes</DialogTitle>
      <DialogContent dividers>
        {reviewOps.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nothing to review.
          </Typography>
        ) : (
          <List disablePadding>
            {reviewOps.map((op) => {
              const isConflict = op.status === 'conflict';
              const summary = summaries[op.id];
              const fields = fieldList(op.payload);
              return (
                <ListItem
                  key={op.id}
                  divider
                  sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, py: 1.5 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip size="small" label={OP_LABEL[op.operationType] || op.operationType} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {ENTITY_LABEL[op.entityType] ?? op.entityType}
                    </Typography>
                    <Chip
                      size="small"
                      color={isConflict ? 'warning' : 'error'}
                      label={isConflict ? 'Conflict' : 'Failed'}
                      sx={{ ml: 'auto' }}
                    />
                  </Box>
                  {summary && (
                    <Typography variant="body2" color="text.primary">
                      {summary}
                    </Typography>
                  )}
                  {op.message && (
                    <Typography variant="caption" color="text.secondary">
                      {op.message}
                    </Typography>
                  )}
                  {fields && op.operationType !== 'delete' && (
                    <Typography variant="caption" color="text.secondary">
                      Your change — {fields}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={1}>
                    {isConflict ? (
                      <>
                        <Button size="small" variant="contained" onClick={() => resolveReview(op.id, 'keepMine')}>
                          Keep mine
                        </Button>
                        <Button size="small" onClick={() => resolveReview(op.id, 'keepServer')}>
                          Use server&apos;s
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="small" variant="contained" onClick={() => resolveReview(op.id, 'retry')}>
                          Retry
                        </Button>
                        <Button size="small" color="error" onClick={() => void handleDiscard(op)}>
                          Discard
                        </Button>
                      </>
                    )}
                  </Stack>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
